import { useState } from 'react';
import { toast } from 'sonner';
import { Check, Pencil, Plus, Star, X } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { useBrands, useBrandMutations, type Brand } from '@/hooks/useBrands';

/**
 * Trading names the company operates under. Technicians see the lead's brand
 * as "Trading as" on every job; they never see the campaign or lead source.
 */
export function BrandsCard() {
  const { data: brands = [], isLoading } = useBrands();
  const { create, update } = useBrandMutations();
  const [newName, setNewName] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');

  const handleAdd = () => {
    const name = newName.trim();
    if (!name) return;
    if (brands.some(b => b.name.toLowerCase() === name.toLowerCase())) {
      toast.error('That trading name already exists');
      return;
    }
    create.mutate(
      { name, makeDefault: brands.length === 0 },
      {
        onSuccess: () => {
          setNewName('');
          toast.success('Trading name added');
        },
        onError: (error) => toast.error(`Could not add trading name: ${error.message}`),
      },
    );
  };

  const startEdit = (brand: Brand) => {
    setEditingId(brand.id);
    setEditingName(brand.name);
  };

  const saveEdit = () => {
    const name = editingName.trim();
    if (!editingId || !name) return;
    update.mutate(
      { id: editingId, name },
      {
        onSuccess: () => {
          setEditingId(null);
          toast.success('Trading name updated');
        },
        onError: (error) => toast.error(`Could not rename: ${error.message}`),
      },
    );
  };

  const setDefault = (brand: Brand) => {
    update.mutate(
      { id: brand.id, is_default: true, is_active: true },
      {
        onSuccess: () => toast.success(`${brand.name} is now the default trading name`),
        onError: (error) => toast.error(`Could not set default: ${error.message}`),
      },
    );
  };

  const toggleActive = (brand: Brand, active: boolean) => {
    if (!active && brand.is_default) {
      toast.error('Pick another default before deactivating this one');
      return;
    }
    update.mutate(
      { id: brand.id, is_active: active },
      { onError: (error) => toast.error(`Could not update: ${error.message}`) },
    );
  };

  return (
    <Card className="p-6">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <h3 className="text-lg font-semibold">Trading names</h3>
          <p className="text-sm text-muted-foreground">
            Each campaign belongs to a trading name. Technicians see it as "Trading as" on the job
            and never see the campaign or lead source. Leads without a campaign use the default.
          </p>
        </div>
      </div>

      <div className="space-y-2">
        {isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
        {!isLoading && brands.length === 0 && (
          <p className="text-sm text-muted-foreground">
            No trading names yet. Add the name your customers know you by; the first one becomes the default.
          </p>
        )}
        {brands.map(brand => (
          <div
            key={brand.id}
            className="flex items-center justify-between gap-3 p-3 bg-muted/50 rounded"
          >
            <div className="flex items-center gap-3 min-w-0 flex-1">
              {editingId === brand.id ? (
                <div className="flex items-center gap-2 flex-1">
                  <Input
                    value={editingName}
                    onChange={(e) => setEditingName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') saveEdit();
                      if (e.key === 'Escape') setEditingId(null);
                    }}
                    className="h-8 max-w-xs"
                    autoFocus
                  />
                  <Button size="sm" variant="ghost" onClick={saveEdit} disabled={update.isPending}>
                    <Check className="h-4 w-4" />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setEditingId(null)}>
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <>
                  <span className={brand.is_active ? 'font-medium' : 'text-muted-foreground line-through'}>
                    {brand.name}
                  </span>
                  {brand.is_default && (
                    <Badge variant="secondary" className="gap-1 text-xs">
                      <Star className="h-3 w-3" /> Default
                    </Badge>
                  )}
                  <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => startEdit(brand)}>
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                </>
              )}
            </div>
            <div className="flex items-center gap-3">
              {!brand.is_default && brand.is_active && (
                <Button size="sm" variant="outline" onClick={() => setDefault(brand)} disabled={update.isPending}>
                  Make default
                </Button>
              )}
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">Active</span>
                <Switch
                  checked={brand.is_active}
                  onCheckedChange={(checked) => toggleActive(brand, checked)}
                  disabled={update.isPending}
                />
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="flex items-center gap-2 mt-4">
        <Input
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleAdd();
          }}
          placeholder="e.g. AAA Garage Doors"
          className="max-w-sm"
        />
        <Button onClick={handleAdd} disabled={!newName.trim() || create.isPending}>
          <Plus className="mr-2 h-4 w-4" />
          Add trading name
        </Button>
      </div>
    </Card>
  );
}
